// @ts-nocheck
/* eslint-disable no-underscore-dangle */
import _ from 'lodash';
import axios from 'axios';
import { create } from 'zustand';
import { getDatabase, onValue, ref, off } from 'firebase/database';
import { API_URL } from '../configs';
import { planNames, toastTypes } from '../enums';
import getRoot from './root';

const blankUser = {
  clientId: null,
  twoFaType: null,
  role: null,
  user_id: null,
  email: null,
  email_verified: null,
  phone_number: null,
  company: null,
  displayTour: null,
  domains: null,
  planName: null,
  accountStatus: null,
  planStartDate: null,
  analytics: null,
  features: {},
};

let authInterceptor = null;
let clientNodeRef = null;
let idToken = null;

const useAuthStore = create((set, get) => ({
  user: blankUser,
  loStatus: typeof window !== 'undefined' ? localStorage.getItem('lo_status') : null,
  loading: false,

  // Computed: `setupStatus` and `paidPlan` are exposed as functions so
  // selectors can subscribe to the underlying state instead.
  setupStatus: () => {
    const { forms } = getRoot();
    return _.isEmpty(get().user.domains) || _.isEmpty(forms ? forms.formLinks : {});
  },
  // Premium feature gate disabled — every account is treated as paid.
  paidPlan: () => true,

  reset: () => {
    set({ user: { ...blankUser }, loading: false });
    if (clientNodeRef) off(clientNodeRef);
    clientNodeRef = null;
    axios.interceptors.request.eject(authInterceptor);
    authInterceptor = null;
    idToken = null;
    localStorage.removeItem('lo_status');
    set({ loStatus: null });
    localStorage.removeItem('summaryData');
    localStorage.removeItem('summaryDataCachedDate');
    localStorage.removeItem('summaryDataTimeSpan');
  },

  setLoStatus: () => {
    localStorage.setItem('lo_status', 'logged_in');
    set({ loStatus: 'logged_in' });
  },

  removeLoStatus: () => {
    localStorage.removeItem('lo_status');
    set({ loStatus: null });
  },

  attachInterceptor: () => {
    authInterceptor = axios.interceptors.request.use((config) => {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = idToken;
      return config;
    }, (err) => console.error('interceptorError', err));
  },

  bindClientNodeToUser: (node = clientNodeRef) => new Promise((resolve, reject) => {
    if (!node) {
      reject(new Error('bindClientNodeToUser called without a client node ref'));
      return;
    }
    let initialCall = false;
    const timeout = setTimeout(() => reject(new Error('Timeout error; exceeded 10s')), 10000);
    onValue(
      node,
      (snapshot) => {
        set((s) => ({ user: { ...s.user, ...(snapshot.val() || {}) } }));
        if (!initialCall) {
          initialCall = true;
          resolve(true);
          clearTimeout(timeout);
        }
      },
      (error) => {
        clearTimeout(timeout);
        reject(error);
      },
    );
  }),

  idTokenCallback: async (firebase, user) => {
    const root = getRoot();
    if (user) {
      get().setLoStatus();
      const idTokenResult = await firebase.auth().currentUser.getIdTokenResult(true);
      const { claims, token } = idTokenResult;
      idToken = token;

      if (claims) {
        set((s) => ({ user: { ...s.user, ...claims } }));
      }

      if (clientNodeRef === null && claims && claims.clientId) {
        try {
          clientNodeRef = ref(getDatabase(), `clients/${claims.clientId}`);
          await get().bindClientNodeToUser();
        } catch (err) {
          // Reset the ref so a retry on the next token change can re-attempt.
          clientNodeRef = null;
          // eslint-disable-next-line no-console
          console.error('bindClientNodeToUser failed:', err);
          root.failure(`Failed to get client data: ${err && err.message ? err.message : 'unknown error'}. Please try refreshing this page.`);
        }
      } else if (clientNodeRef === null && claims && !claims.clientId) {
        // No clientId in claims yet — likely a brand-new user whose setup
        // call hasn't completed. Don't toast; just wait for the next token
        // refresh which should arrive after /api/um/setup sets claims.
        // eslint-disable-next-line no-console
        console.warn('idTokenCallback: claims.clientId is undefined — waiting for setup to complete.');
      }
      // Always mark app as initialized so gated UI (e.g., Dashboard spinner)
      // can render. initAppState internally guards on clientId for API calls.
      root.initAppState();
    } else {
      root.resetAppState();
      get().removeLoStatus();
      localStorage.removeItem('summaryData');
      localStorage.removeItem('summaryDataCachedDate');
      localStorage.removeItem('summaryDataTimeSpan');
    }
  },

  register: async (email, password, userInfo) => {
    set({ loading: true });
    const firebase = getRoot().firebase;
    const userObj = await firebase.auth().createUserWithEmailAndPassword(email, password);
    if (userObj) {
      const { uid } = userObj.user;
      await axios.post(`${API_URL}/api/um/setup`, { uid, userInfo });
      get().setLoStatus();
    }
    set({ loading: false });
  },

  login: async (email, password) => {
    set({ loading: true });
    const userObj = await getRoot().firebase.auth().signInWithEmailAndPassword(email, password);
    if (userObj) get().setLoStatus();
    set({ loading: false });
  },

  customLogin: async (token) => {
    await getRoot().firebase.auth().signInWithCustomToken(token);
  },

  shopifyLogin: async (qs) => {
    const root = getRoot();
    set({ loading: true });
    try {
      const { data } = await axios.get(`${API_URL}/api/integrations/Shopify/auth${qs}`);
      await get().customLogin(data.idToken);
    } catch (e) {
      root.failure('Could not login with Shopify.');
    }
    set({ loading: false });
  },

  wixLogin: async (instance) => {
    const root = getRoot();
    set({ loading: true });
    try {
      const { data } = await axios.get(`${API_URL}/api/integrations/Wix/custom-login?instance=${instance}`);
      await get().customLogin(data.idToken);
    } catch (err) {
      get().signOut();
      root.failure('Could not login through Wix Dashboard. Please login or register if you have not done so.');
    }
    const integrations = root.integrations;
    if (integrations && integrations.WiX && integrations.WiX.linkAccounts) {
      integrations.WiX.linkAccounts(instance);
    }
    set({ loading: false });
  },

  salesforceMarketincCloudLogin: async (code) => {
    const root = getRoot();
    set({ loading: true });
    try {
      const { data } = await axios.get(`${API_URL}/api/integrations/SalesforceMarketingCloud/verify?code=${code}`);
      await get().customLogin(data.idToken);
    } catch (err) {
      root.failure('Could not login through Salesforce Marketing Cloud Dashboard. Please login or register if you have not done so.');
    }
    set({ loading: false });
  },

  sendCustomVerificationEmail: async (email) => {
    const root = getRoot();
    set({ loading: true });
    try {
      await axios.post(`${API_URL}/api/um/custom-verification-email`, { email });
      root.success('Verification email sent!', toastTypes.sendCustomVerificationEmail);
      root.clearToast(toastTypes.sendCustomVerificationEmail);
    } catch (e) {
      root.failure('Could not send verification email.', toastTypes.sendCustomVerificationEmail);
    } finally {
      set({ loading: false });
    }
  },

  sendCustomPasswordResetEmail: async (email) => {
    const root = getRoot();
    set({ loading: true });
    try {
      await axios.post(`${API_URL}/api/um/custom-pw-reset`, { email });
      root.success('Password reset email sent!', toastTypes.sendCustomPasswordResetEmail);
      root.clearToast(toastTypes.sendCustomPasswordResetEmail);
    } catch (e) {
      root.failure('Could not send password reset email.', toastTypes.sendCustomPasswordResetEmail);
    } finally {
      set({ loading: false });
    }
  },

  signOut: () => {
    getRoot().firebase.auth().signOut();
    get().removeLoStatus();
    localStorage.removeItem('summaryData');
    localStorage.removeItem('summaryDataCachedDate');
    localStorage.removeItem('summaryDataTimeSpan');
    if (typeof window !== 'undefined') {
      window.location.assign('/');
    }
  },

  grabAllClaims: async (forceRefresh = true) => {
    const t = await getRoot().firebase.auth().currentUser.getIdTokenResult(forceRefresh);
    return t.claims;
  },

  sendCode: async () => {
    try {
      await axios.post(`${API_URL}/api/auth/send-2fa-code`);
      getRoot().success('Code sent!');
    } catch (err) {
      console.error(err);
    }
  },

  editTwoFa: async (twoFaType, twoFaCode, phoneNumber) => {
    set({ loading: true });
    try {
      await axios.post(`${API_URL}/api/auth/edit-2fa`, { twoFaType, twoFaCode, phoneNumber });
      await get().grabAllClaims();
      return { success: true };
    } catch (err) {
      return { error: `Couldn't edit 2FA ${twoFaType}` };
    } finally {
      set({ loading: false });
    }
  },

  verifyCode: async (twoFaCode) => {
    const root = getRoot();
    set({ loading: true });
    try {
      const res = await axios.post(`${API_URL}/api/auth/verify`, { twoFaCode });
      const { token: customToken } = res.data;
      if (customToken) {
        await root.firebase.auth().signInWithCustomToken(customToken);
        await root.firebase.auth().currentUser.getIdTokenResult(true);
        root.success('Successfully verified code.', toastTypes.verifyCode);
        root.clearToast(toastTypes.verifyCode);
        return { success: true };
      }
      return { success: false };
    } catch (e) {
      root.failure('Could not verify code.', toastTypes.verifyCode);
      return { success: false };
    } finally {
      set({ loading: false });
    }
  },

  updateClaims: async (propertiesToUpdate, twoFaCode) => {
    try {
      await axios.post(`${API_URL}/api/auth/update-user`, { ...propertiesToUpdate, twoFaCode });
      return get().grabAllClaims();
    } catch (err) {
      return { error: 'Couldn\'t update the properties.' };
    }
  },

  updateClientNodeData: async (clientId = get().user.clientId) => {
    const node = ref(getDatabase(), `clients/${clientId}`);
    onValue(node, (snapshot) => {
      const data = snapshot.val() || {};
      set((s) => ({ user: { ...s.user, ...data } }));
    });
  },
}));

export default useAuthStore;
